import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'wouter';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { 
  Users, 
  FileText, 
  DollarSign, 
  BarChart3, 
  CheckCircle,
  ArrowRight,
  Phone,
  Shield,
  Calendar,
  TrendingUp,
  Zap,
  Sparkles,
  Bot,
  Send,
  Download,
  Eye,
  Clock,
  Target,
  Award,
  X,
  Timer,
  Building2,
  Wrench,
  Hammer,
  MapPin,
  LayoutDashboard,
  ClipboardList
} from 'lucide-react';

// Animated Counter Component
function StatCounter({ end, suffix = '', prefix = '', label, duration = 2000 }: { 
  end: number; 
  suffix?: string; 
  prefix?: string; 
  label: string; 
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const countRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
          let startTime: number | null = null;
          const animate = (currentTime: number) => {
            if (startTime === null) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / duration, 1);
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(end * easeOutQuart));
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [end, duration, hasStarted]);

  return (
    <div className="text-center scroll-animate" ref={countRef}>
      <div
        className="text-4xl md:text-5xl font-bold mb-2 transition-all duration-300 hover:scale-110"
        style={{ color: '#134e4a' }}
      >
        {prefix}{count.toLocaleString()}{suffix}
      </div>
      <div
        className="text-sm md:text-base font-semibold"
        style={{ color: '#334155' }}
      >
        {label}
      </div>
    </div>
  );
}

export default function Landing() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);
    
    // Mouse parallax effect
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach(el => observer.observe(el));

    // Scroll progress indicator
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      elements.forEach(el => observer.unobserve(el));
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Remodra – Construction Management Software for Contractors</title>
        <meta name="description" content="Manage clients, estimates, invoices, projects, and scheduling in one place. Contractor software that helps you win more bids and get paid faster." />
        <link rel="canonical" href="https://getremodra.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "What is Remodra?", acceptedAnswer: { "@type": "Answer", text: "Remodra is construction management software for contractors. It helps you manage clients, create and send estimates and invoices, run projects with Kanban boards, and give clients a secure portal. AI writes professional estimate descriptions in seconds." } },
              { "@type": "Question", name: "Who is Remodra for?", acceptedAnswer: { "@type": "Answer", text: "Remodra is for contractors, remodelers, and construction businesses who want to look professional, win more bids with AI-powered estimates, and get paid faster with organized invoicing and a client portal." } },
              { "@type": "Question", name: "Is there a free trial?", acceptedAnswer: { "@type": "Answer", text: "Yes. You can start a free trial with no credit card required. Choose Basic, Pro, or Business and upgrade or change plans anytime." } },
              { "@type": "Question", name: "What plans does Remodra offer?", acceptedAnswer: { "@type": "Answer", text: "Remodra offers Basic ($49/mo), Pro ($79/mo with AI estimates and roof reports), and Business ($149/mo with full team and agent management). All plans include a free trial." } },
              { "@type": "Question", name: "Can my clients view estimates and invoices online?", acceptedAnswer: { "@type": "Answer", text: "Yes. Clients get secure, token-based access to a portal where they can view estimates, approve them, see project status, and view or pay invoices 24/7." } }
            ]
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden">
      {/* Scroll Progress - subtle */}
      <div className="fixed top-0 left-0 w-full h-0.5 bg-slate-200/80 z-50">
        <div 
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300 rounded-r-full"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Header - light, clean */}
      <header className="relative z-10 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-start md:items-center justify-between h-auto md:h-20 gap-3 py-3 md:py-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <img src="/remodra-logo.png" alt="Remodra" className="h-6 w-6 md:h-8 md:w-8 rounded object-cover" />
              </div>
              <span className="text-slate-900 text-xl md:text-2xl font-bold tracking-tight">Remodra</span>
            </div>
            <div className="flex flex-col items-end gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:items-center">
              <Link to="/pricing">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium px-3 md:px-4 h-8 md:h-9 text-sm">
                  Pricing
                </Button>
              </Link>
              <Link to="/roofing-software">
                <Button variant="ghost" className="hidden md:inline-flex text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium px-3 md:px-4 h-9 md:h-10">
                  Roofing
                </Button>
              </Link>
              <Link to="/contractor-estimating-software">
                <Button variant="ghost" className="hidden md:inline-flex text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium px-3 md:px-4 h-9 md:h-10">
                  AI Estimating
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium px-3 md:px-4 h-8 md:h-9 text-sm">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold px-5 md:px-7 h-9 md:h-10 rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200 text-sm md:text-base">
                  Get Started
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero - bright gradient + 3D shapes (light theme) */}
      <section
        className="relative min-h-[85vh] flex flex-col items-center justify-center z-10 overflow-hidden landing-perspective"
        ref={heroRef}
        style={{
          background: 'linear-gradient(165deg, #ffffff 0%, #f0fdf4 25%, #ecfdf5 55%, #d1fae5 85%, #ccfbf1 100%)',
        }}
      >
        {/* 3D floating shapes - soft mint/teal on light */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" aria-hidden="true">
          <motion.div
            className="absolute top-[18%] left-[12%] w-40 h-40 md:w-52 md:h-52 landing-shape-3d animate-float-3d"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(20, 184, 166, 0.18) 100%)',
              borderRadius: '24px',
              boxShadow: '0 20px 40px -12px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
              transform: 'rotateX(12deg) rotateY(-10deg)',
            }}
          />
          <motion.div
            className="absolute top-[55%] right-[8%] w-32 h-32 md:w-44 md:h-44 landing-shape-3d animate-float-3d-slow"
            style={{
              background: 'linear-gradient(145deg, rgba(52, 211, 153, 0.15) 0%, rgba(45, 212, 191, 0.2) 100%)',
              borderRadius: '20px',
              boxShadow: '0 16px 32px -10px rgba(16, 185, 129, 0.12)',
              transform: 'rotateX(-8deg) rotateY(12deg)',
            }}
          />
          <motion.div
            className="absolute bottom-[22%] left-[8%] w-28 h-28 md:w-36 md:h-36 landing-shape-3d animate-float-3d"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(13, 148, 136, 0.2) 100%)',
              borderRadius: '16px',
              boxShadow: '0 12px 24px -8px rgba(20, 184, 166, 0.2)',
              transform: 'rotateX(5deg) rotateY(15deg)',
            }}
          />
          <motion.div
            className="absolute top-[35%] right-[22%] w-20 h-20 md:w-24 md:h-24 landing-shape-3d animate-float-3d-slow"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(255,255,255,0.5) 60%)',
              borderRadius: '12px',
              boxShadow: '0 8px 20px -6px rgba(16, 185, 129, 0.15)',
              transform: 'rotateX(-15deg) rotateY(-5deg)',
            }}
          />
        </div>
        {/* Central glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] max-w-4xl h-3/4 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.06) 50%, transparent 70%)',
          }}
          aria-hidden="true"
        />
        {/* Shine sweep */}
        <div className="absolute inset-0 pointer-events-none hero-shine-animate opacity-90" aria-hidden="true" />

        <motion.div
          className="container relative z-10 mx-auto px-4 py-16 md:py-24 text-center max-w-4xl"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
            hidden: {},
          }}
        >
          <motion.div
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-400/30"
          >
            <Sparkles className="h-4 w-4 text-emerald-600" />
            AI-first contractor platform
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight text-slate-900"
            style={{ letterSpacing: '-0.02em' }}
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          >
            Construction management software for{' '}
            <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 bg-clip-text text-transparent">contractors</span>{' '}
            with built-in AI roof measurement
          </motion.h1>

          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed font-medium"
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
          >
            All-in-one contractor platform to manage clients, estimates, invoices, projects, and teams—with AI that writes estimate descriptions and generates roof measurements from just an address.
          </motion.p>

          <motion.div
            className="flex flex-wrap justify-center gap-3 mb-8"
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
          >
            {[
              { icon: Bot, label: 'AI-Powered' },
              { icon: Users, label: 'Client Portal' },
              { icon: BarChart3, label: 'Kanban' },
              { icon: DollarSign, label: 'Payments' },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2.5 text-sm font-semibold text-emerald-700 shadow-md ring-1 ring-emerald-200/60 hover:shadow-lg hover:ring-emerald-300/80 transition-all"
              >
                <Icon className="h-4 w-4 text-emerald-600" />
                {label}
              </span>
            ))}
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
          >
            <Link to="/register">
              <Button
                size="lg"
                className="px-8 py-4 rounded-xl font-bold text-base bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-[0_4px_20px_rgba(16,185,129,0.35)] hover:shadow-[0_6px_28px_rgba(16,185,129,0.45)] hover:scale-[1.02] transition-all duration-300 group"
              >
                Start free trial
                <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform inline" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              className="bg-white border-2 border-emerald-500/60 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-500 font-semibold px-8 py-4 rounded-xl shadow-sm transition-all"
              onClick={() => window.open(`${window.location.origin}/schedule-demo`, '_blank', 'noopener,noreferrer')}
            >
              <Phone className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              Schedule demo
            </Button>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-emerald-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="w-7 h-11 rounded-full border-2 border-emerald-400/60 flex justify-center pt-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-scroll-bounce" />
          </div>
        </motion.div>
      </section>

      {/* Section 2: AI-Powered Estimates - light, no divider */}
      <section className="relative z-10 py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-8">AI-Powered Technology</p>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate">
              <span className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-700 font-semibold text-sm ring-1 ring-emerald-400/30">
                <Bot className="h-4 w-4" />
                AI-Powered Technology
                <Sparkles className="h-4 w-4" />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                AI Writes Your <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Estimate Descriptions</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-4 leading-relaxed">
                Stop spending hours writing estimate descriptions. Our AI (powered by Google Gemini) turns your technical notes into professional, client-friendly descriptions that win more jobs.
              </p>
              <p className="text-sm text-slate-500 mb-8">
                Want to go deeper?{' '}
                <Link to="/contractor-estimating-software" className="text-emerald-700 font-semibold hover:underline">
                  See AI estimating in action
                </Link>
                {' '}or, if you&apos;re a roofing company, check out our{' '}
                <Link to="/roofing-software" className="text-emerald-700 font-semibold hover:underline">
                  Roofing software page
                </Link>
                .
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Write in seconds, not hours</p>
                    <p className="text-sm text-slate-600">Enter your notes, AI generates professional descriptions instantly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Powered by Google Gemini</p>
                    <p className="text-sm text-slate-600">Advanced AI technology for reliable, professional descriptions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Client-friendly language</p>
                    <p className="text-sm text-slate-600">Translates technical jargon into clear, persuasive descriptions</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register?plan=basic">
                <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-xl transition-all group">
                  <Zap className="mr-2 h-4 w-4 group-hover:animate-pulse" />
                  Try AI Estimates Free
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5" />
                </Button>
              </Link>
            </div>
            <div className="relative scroll-animate">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200/80 relative overflow-hidden ring-1 ring-slate-200/50">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-teal-500/5 pointer-events-none rounded-2xl" />
                <div className="relative z-10 space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                        <Bot className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">AI Estimate Generator</p>
                        <p className="text-xs text-slate-500">Powered by Google Gemini</p>
                      </div>
                    </div>
                    <div className="px-3 py-1 bg-emerald-500/15 rounded-full">
                      <span className="text-xs font-semibold text-emerald-700">Active</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-2">Your Notes:</p>
                    <p className="text-sm text-slate-700 italic">"Replace 340ft gutters, black 6-inch, add downspouts"</p>
                  </div>
                  <div className="flex items-center justify-center py-4">
                    <div className="flex items-center gap-2 text-emerald-600">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce delay-200" />
                      <span className="ml-2 text-sm font-medium">AI Generating...</span>
                    </div>
                  </div>
                  <div className="bg-emerald-50/80 rounded-xl p-4 border border-emerald-200/60">
                    <p className="text-xs font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI Generated Description:
                    </p>
                    <ul className="space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 mt-1">•</span>
                        <span>Replacement of 340 linear feet of existing gutters with new black 6-inch gutters</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 mt-1">•</span>
                        <span>Installation of downspouts to ensure proper drainage and water flow</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 mt-1">•</span>
                        <span>Protect landscaping and surrounding areas with tarps before starting work</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 mt-1">•</span>
                        <span>Complete thorough cleanup of work area, removing all debris</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                      <span>Professional tone</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                      <span>Client-friendly</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                      <span>Ready to send</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white rounded-full p-3 shadow-xl border border-slate-200 animate-float ring-2 ring-emerald-400/30">
                <Zap className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-full p-3 shadow-xl border border-slate-200 animate-float delay-1000 ring-2 ring-teal-400/30">
                <Sparkles className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2b: AI Roof Reports from Address Only */}
      <section className="relative z-10 py-20 md:py-28 bg-slate-50/80">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-8">Pro & Business</p>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative scroll-animate order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200/80 relative overflow-hidden ring-1 ring-slate-200/50">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-emerald-500/5 pointer-events-none rounded-2xl" />
                <div className="relative z-10 space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/20">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900">Roof Report by Address</p>
                        <p className="text-xs text-slate-500">No photos required</p>
                      </div>
                    </div>
                    <Badge className="bg-teal-500/15 text-teal-700 border-teal-500/30">AI</Badge>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-2">Enter any property address</p>
                    <p className="text-sm text-slate-700 font-mono">123 Main St, Anytown USA</p>
                  </div>
                  <div className="flex items-center justify-center py-2">
                    <ArrowRight className="h-5 w-5 text-teal-500" />
                  </div>
                  <div className="bg-teal-50/80 rounded-xl p-4 border border-teal-200/60">
                    <p className="text-xs font-semibold text-teal-700 mb-2">AI Roof Report</p>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-700">
                      <span className="font-semibold text-teal-700">2,340 sqft</span>
                      <span>•</span>
                      <span>94% confidence</span>
                      <span>•</span>
                      <span>Pitch & sections</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="scroll-animate order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-teal-500/10 text-teal-700 font-semibold text-sm ring-1 ring-teal-400/30">
                <MapPin className="h-4 w-4" />
                Address-only roof reports
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                AI Roof Measurement from <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">Just an Address</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                No ladder, no photos. Enter a property address and get an AI-powered roof report: square footage, pitch, sections, and confidence score—powered by aerial and satellite data. Perfect for quick quotes and Pro/Business plans.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">No photos or site visit needed</p>
                    <p className="text-sm text-slate-600">Uses address and aerial imagery to analyze the roof</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Square footage & confidence score</p>
                    <p className="text-sm text-slate-600">Get sqft, pitch, and section breakdown in seconds</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Included in Pro & Business</p>
                    <p className="text-sm text-slate-600">
                      5 reports/month included, then <span className="line-through">$8 per report</span> - now completely free
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/register?plan=pro">
                <Button size="lg" className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-teal-500/25 hover:shadow-xl transition-all group">
                  <MapPin className="mr-2 h-4 w-4 group-hover:animate-pulse" />
                  Try Roof Reports Free
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Stats - soft gradient, same style, credible numbers */}
      <section className="relative z-10 py-20 md:py-24 overflow-hidden landing-perspective" style={{ background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 50%, #d1fae5 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/2 left-[10%] w-24 h-24 md:w-32 md:h-32 landing-shape-3d animate-float-3d-slow rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(20,184,166,0.12) 100%)', boxShadow: '0 12px 24px -8px rgba(16,185,129,0.12)' }} />
          <div className="absolute top-1/3 right-[12%] w-20 h-20 md:w-28 md:h-28 landing-shape-3d animate-float-3d rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.1) 0%, rgba(45,212,191,0.08) 100%)', boxShadow: '0 10px 20px -6px rgba(52,211,153,0.15)' }} />
        </div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10" style={{ color: '#0f172a' }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-10 text-center" style={{ color: '#134e4a' }}>By the numbers</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10" role="list" aria-label="Key metrics">
            <StatCounter end={100} suffix="+" label="Active Contractors" />
            <StatCounter end={10000} suffix="+" label="Invoices Sent" />
            <StatCounter end={200} suffix="+" label="Team Members Onboarded" />
            <StatCounter end={98} suffix="%" label="On-Time Delivery" />
          </div>
        </div>
      </section>

      {/* Section 4: How It Works - no divider */}
      <section className="relative z-10 py-20 md:py-28 bg-slate-50/80">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-6 text-center">Getting started</p>
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold text-sm ring-2 ring-emerald-400/40">Getting started</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Get started in minutes and transform your business workflow
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-10 md:gap-12">
            <div className="text-center scroll-animate relative">
              <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20 relative z-10">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-emerald-400/50 to-transparent hidden sm:block" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Sign Up</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                Create your account and choose your plan. Free trial — no credit card required.
              </p>
            </div>
            <div className="text-center scroll-animate relative">
              <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20 relative z-10">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-emerald-400/50 to-transparent hidden sm:block" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Set Up</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                Import clients, customize settings, and configure workflows in minutes.
              </p>
            </div>
            <div className="text-center scroll-animate relative">
              <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20 relative z-10">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Grow</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                Manage estimates, projects, and invoices. Watch your efficiency soar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Client Portal - soft gradient, no divider */}
      <section className="relative z-10 py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f0fdf4 50%, #ecfdf5 100%)' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-8">Client Portal</p>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative scroll-animate order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-emerald-100 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-emerald-500/5" />
                <div className="relative z-10 space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">Client Portal</p>
                        <p className="text-xs text-slate-500">Secure 24/7 Access</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Active</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800">Project: Kitchen Remodel</span>
                        <span className="text-xs text-green-600 font-medium">In Progress</span>
                      </div>
                      <div className="text-xs text-slate-600">View progress, timeline, and updates</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800">Estimate #1234</span>
                        <span className="text-xs text-blue-600 font-medium">Pending</span>
                      </div>
                      <div className="text-xs text-slate-600">Review and approve estimate</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800">Invoice #5678</span>
                        <span className="text-xs text-green-600 font-medium">Paid</span>
                      </div>
                      <div className="text-xs text-slate-600">Download receipt and payment history</div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200 flex items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3 text-green-500" />
                      <span>Secure Access</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3 text-green-500" />
                      <span>Real-time Updates</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="scroll-animate order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-700 font-semibold text-sm ring-1 ring-emerald-400/20">
                <Users className="h-4 w-4" />
                Client Portal
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Give Clients <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">24/7 Access</span>
              </h2>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Your clients get their own secure portal to view estimates, track projects, see invoices, and download documents. No more "where's my invoice?" calls.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Secure token-based access</p>
                    <p className="text-sm text-slate-600">Clients access via secure email links - no passwords needed</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Real-time project updates</p>
                    <p className="text-sm text-slate-600">Clients see project status, timeline, and progress instantly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Document downloads</p>
                    <p className="text-sm text-slate-600">PDF estimates, invoices, and receipts available 24/7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Features - no divider */}
      <section className="relative z-10 py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-6 text-center">Powerful Features</p>
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold text-sm ring-2 ring-emerald-400/40">Powerful Features</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Built to Help You Make More
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Every feature is designed to save time, look professional, and win more bids — so you can focus on the work that pays.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-xl hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1 scroll-animate group cursor-pointer relative overflow-hidden">
            {/* Hover gradient effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            {/* Shine effect on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </div>
            <CardHeader className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 group-hover:shadow-green-500/50 group-hover:shadow-xl">
                <FileText className="h-7 w-7 text-white group-hover:animate-pulse" />
              </div>
              <CardTitle className="text-xl text-slate-800 group-hover:text-green-600 transition-colors flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-600 group-hover:animate-pulse" />
                AI-Powered Estimates
              </CardTitle>
              <CardDescription className="text-slate-600">
                Google Gemini writes professional descriptions from your notes in seconds
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  <span className="font-semibold">Google Gemini</span> AI generation
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  GPT-4o-mini backup system
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Email with secure token links
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  One-click estimate to invoice
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Professional PDF templates
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 scroll-animate group cursor-pointer relative overflow-hidden hover:border-green-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </div>
            <CardHeader className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 group-hover:shadow-green-500/50 group-hover:shadow-xl">
                <DollarSign className="h-7 w-7 text-white group-hover:animate-pulse" />
              </div>
              <CardTitle className="text-xl text-slate-800 group-hover:text-green-600 transition-colors">Invoicing & Payments</CardTitle>
              <CardDescription className="text-slate-600">
                Get paid faster with professional invoices and complete payment tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Generate invoices from estimates instantly
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Track payments and outstanding balances
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Full payment history per invoice
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Share invoices with clients via email and portal
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 scroll-animate group cursor-pointer relative overflow-hidden hover:border-green-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </div>
            <CardHeader className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 group-hover:shadow-green-500/50 group-hover:shadow-xl">
                <Users className="h-7 w-7 text-white group-hover:animate-pulse" />
              </div>
              <CardTitle className="text-xl text-slate-800 group-hover:text-green-600 transition-colors">Client Management</CardTitle>
              <CardDescription className="text-slate-600">
                Keep all client information organized and accessible in one place
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Secure client portal for 24/7 access
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Track all client notes and activity
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Complete project and invoice history
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Search and filter clients instantly
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 scroll-animate group cursor-pointer relative overflow-hidden hover:border-green-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </div>
            <CardHeader className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 group-hover:shadow-green-500/50 group-hover:shadow-xl">
                <BarChart3 className="h-7 w-7 text-white group-hover:animate-pulse" />
              </div>
              <CardTitle className="text-xl text-slate-800 group-hover:text-green-600 transition-colors">Project Management</CardTitle>
              <CardDescription className="text-slate-600">
                Visual Kanban boards with service-specific workflows for every project type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  14 service-specific workflows (Roofing, Fencing, etc.)
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Drag-and-drop project tracking
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Timeline management with dates
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Project notes and activity logs
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 scroll-animate group cursor-pointer relative overflow-hidden hover:border-green-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </div>
            <CardHeader className="relative z-10">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 group-hover:shadow-green-500/50 group-hover:shadow-xl">
                <Calendar className="h-7 w-7 text-white group-hover:animate-pulse" />
              </div>
              <CardTitle className="text-xl text-slate-800 group-hover:text-green-600 transition-colors">Calendar & Scheduling</CardTitle>
              <CardDescription className="text-slate-600">
                Schedule appointments, assign agents, and never miss a client meeting
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Create and manage appointments
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Assign agents to events
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Multiple view modes (month, week, day)
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  Reassign and reschedule easily
                </li>
              </ul>
            </CardContent>
          </Card>
          </div>
        </div>
      </section>

      {/* Section 6b: App preview / Screenshots - browser mockups */}
      <section className="relative z-10 py-20 md:py-28 overflow-hidden" style={{ background: 'linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 50%, #fff 100%)' }}>
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-6 text-center">See it in action</p>
          <div className="text-center mb-14 scroll-animate">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Built for How You Work
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              One place for clients, estimates, projects, and AI—so you look pro and get paid faster.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-start">
            {/* Browser mockup: Dashboard */}
            <motion.div
              className="scroll-animate rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                </div>
                <span className="text-xs text-slate-500 ml-2 truncate flex-1">getremodra.com/dashboard</span>
              </div>
              <div className="p-4 space-y-3 min-h-[200px] bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <LayoutDashboard className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-slate-800">Dashboard</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {['Net Income', 'Orders', 'Avg Contract', 'Growth'].map((label, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm">
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className="text-lg font-bold text-slate-800">{i === 0 ? '$12.4k' : i === 1 ? '24' : i === 2 ? '$1.2k' : '+12%'}</p>
                    </div>
                  ))}
                </div>
                <div className="h-2 w-3/4 bg-slate-100 rounded" />
                <div className="flex gap-2">
                  <div className="h-8 flex-1 bg-emerald-100 rounded" />
                  <div className="h-8 w-16 bg-slate-100 rounded" />
                </div>
              </div>
            </motion.div>
            {/* Browser mockup: Estimate */}
            <motion.div
              className="scroll-animate rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 md:mt-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                </div>
                <span className="text-xs text-slate-500 ml-2 truncate flex-1">getremodra.com/estimates</span>
              </div>
              <div className="p-4 space-y-3 min-h-[200px] bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-slate-800">Estimate</span>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Description</p>
                  <p className="text-sm text-slate-700 italic">AI-generated from your notes...</p>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Gutter replacement</span>
                  <span className="font-semibold text-slate-800">$2,450</span>
                </div>
                <div className="flex gap-2 pt-2">
                  <div className="h-8 flex-1 bg-emerald-500 rounded-lg" />
                  <div className="h-8 w-20 bg-slate-200 rounded-lg" />
                </div>
              </div>
            </motion.div>
            {/* Browser mockup: Client Portal */}
            <motion.div
              className="scroll-animate rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                </div>
                <span className="text-xs text-slate-500 ml-2 truncate flex-1">Client portal</span>
              </div>
              <div className="p-4 space-y-3 min-h-[200px] bg-gradient-to-br from-slate-50 to-white">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-slate-800">Client view</span>
                </div>
                <div className="space-y-2">
                  {['Kitchen Remodel · In progress', 'Estimate #1234 · Pending', 'Invoice · Paid'].map((line, i) => (
                    <div key={i} className="bg-white rounded-lg p-2.5 border border-slate-100 flex justify-between items-center">
                      <span className="text-xs text-slate-700 truncate">{line}</span>
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 7: Pricing - soft gradient, no divider */}
      <section className="relative z-10 py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #f8fffe 0%, #f0fdf4 50%, #ecfdf5 100%)' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-8 text-center">Simple Pricing</p>
          <div className="text-center mb-12 scroll-animate">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold text-sm ring-2 ring-emerald-400/40">Simple Pricing</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Tools That Help You Make More
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Every plan is built to save you time and win more jobs. Start free, scale when you're ready.
            </p>
          </div>
        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 overflow-visible md:items-stretch">
          {/* Basic Plan */}
          <Card className="flex flex-col h-full bg-white border-2 border-slate-200 shadow-lg hover:shadow-xl hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1 scroll-animate relative cursor-pointer group overflow-hidden rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-600/0 group-hover:from-green-500/5 group-hover:to-green-600/5 transition-all duration-500 pointer-events-none"></div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-slate-800">Basic</CardTitle>
              <div className="text-4xl font-bold text-slate-800">
                $49<span className="text-lg text-slate-600">/month</span>
              </div>
              <CardDescription className="text-slate-600">
                Get organized and look professional — win trust from day one
              </CardDescription>
        </CardHeader>
            <CardContent className="flex flex-col flex-1 min-h-0 pt-6 space-y-4">
              <ul className="space-y-3 text-sm text-slate-600 flex-1">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  1 user included · +$11/user for team
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Clients, estimates & invoices
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Project management & client portal
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Email support
                </li>
              </ul>
              <div className="pt-4 mt-auto">
                <Link to="/register?plan=basic">
                  <Button className="w-full bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold rounded-xl shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)]">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="flex flex-col h-full bg-white border-2 border-emerald-500 shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/25 transition-all duration-300 hover:-translate-y-1 scroll-animate relative rounded-2xl cursor-pointer group overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-center py-3 rounded-t-2xl">
              <span className="text-sm font-semibold">Most Popular · Win More Bids</span>
            </div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-slate-800">Pro</CardTitle>
              <div className="text-4xl font-bold text-slate-800">
                $79<span className="text-lg text-slate-600">/month</span>
              </div>
              <CardDescription className="text-slate-600">
                AI that writes estimates & roof reports — close more deals, faster
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 min-h-0 pt-6 space-y-4">
              <ul className="space-y-3 text-sm text-slate-600 flex-1">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  1 user included · +$11/user for team
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  5 AI roof reports/month included · <span className="line-through">$8 each after</span> · now completely free
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  AI estimate descriptions & project tasks
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Calendar, time clock, priority support
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  All Basic features
                </li>
              </ul>
              <div className="pt-4 mt-auto">
                <Link to="/register?plan=pro">
                  <Button className="w-full bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold rounded-xl shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)]">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Business Plan */}
          <Card className="flex flex-col h-full bg-white border-2 border-slate-200 shadow-lg hover:shadow-xl hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1 scroll-animate relative rounded-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-slate-800">Business</CardTitle>
              <div className="text-4xl font-bold text-slate-800">
                $149<span className="text-lg text-slate-600">/month</span>
              </div>
              <CardDescription className="text-slate-600">
                Full team features and control as you grow
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 min-h-0 pt-6 space-y-4">
              <ul className="space-y-3 text-sm text-slate-600 flex-1">
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  1 user included · +$11/user for team
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  5 AI roof reports/month included · <span className="line-through">$8 each after</span> · now completely free
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Advanced client portal experience for bigger teams
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Team/agents management & full timeclock
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Premium support
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  All Pro features
                </li>
              </ul>
              <div className="pt-4 mt-auto">
                <Link to="/register?plan=business">
                  <Button className="w-full bg-gradient-to-r from-emerald-600 to-green-500 text-white font-semibold rounded-xl shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)]">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="text-center mt-8">
          <Link to="/pricing">
            <Button variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-medium">
              Compare all plans & features →
            </Button>
          </Link>
        </div>
        </div>
      </section>

      {/* Section 8: Testimonials - no divider */}
      <section className="relative z-10 py-20 md:py-28 bg-slate-50/80">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-6 text-center">Testimonials</p>
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-block mb-4 px-4 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 font-semibold text-sm ring-2 ring-emerald-400/40">Trusted by Professionals</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why Contractors Love Remodra
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
              Look more professional, work more efficiently, and get paid faster.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <TrendingUp className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Win More Jobs
              </h3>
              <p className="text-slate-600 leading-relaxed">
                AI writes professional estimate descriptions that explain your work clearly. Clients understand what they're paying for, and you close more deals.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <DollarSign className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Reduce Costs
              </h3>
              <p className="text-slate-600">
                AI-powered cost analysis helps you create accurate estimates, avoid costly mistakes, and maximize your profit margins on every project.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <Users className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Improve Client Relationships
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Give your clients 24/7 access to their projects, estimates, and invoices through a professional client portal. Better communication builds trust and repeat business.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Scale Your Business
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Manage unlimited clients and projects from a single platform. As your business grows, Remodra grows with you - no need to switch systems.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Stay Organized
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Visual Kanban boards show exactly where every project stands. Drag and drop to update status. No more wondering what's next or what's behind schedule.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-emerald-100 transition-all duration-300 hover:-translate-y-1 scroll-animate rounded-2xl">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                <Shield className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                Enterprise Security
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Your business data is protected with bank-level encryption and secure backups. Focus on growing your business with confidence.
              </p>
            </CardContent>
          </Card>
          </div>
        </div>
      </section>

      {/* Section 9: CTA - gradient only, no divider */}
      <section className="relative z-10 py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #ccfbf1 0%, #99f6e4 40%, #5eead4 100%)' }}>
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-6">Ready to start?</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Ready to Win More Jobs and Get Paid Faster?
          </h2>
          <p className="text-lg sm:text-xl text-slate-700 mb-10 max-w-xl mx-auto">
            Join thousands of contractors using Remodra to streamline operations and grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/register">
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-8 py-4 rounded-xl shadow-xl shadow-emerald-500/25 hover:scale-[1.02] transition-all group"
              >
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              className="bg-white border-2 border-slate-700 text-slate-800 font-semibold px-8 py-4 rounded-xl hover:bg-slate-100 transition-all"
              onClick={() => window.open(`${window.location.origin}/schedule-demo?contact=sales`, '_blank', 'noopener,noreferrer')}
            >
              <Phone className="mr-2 h-5 w-5" />
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Section: FAQ - for SEO and conversion */}
      <section className="relative z-10 py-20 md:py-28 bg-white" aria-label="Frequently asked questions">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-6 text-center">FAQ</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            <div className="scroll-animate">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">What is Remodra?</h3>
              <p className="text-slate-600 leading-relaxed">Remodra is construction management software for contractors. It helps you manage clients, create and send estimates and invoices, run projects with Kanban boards, and give clients a secure portal. AI writes professional estimate descriptions in seconds.</p>
            </div>
            <div className="scroll-animate">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Who is Remodra for?</h3>
              <p className="text-slate-600 leading-relaxed">Remodra is for contractors, remodelers, and construction businesses who want to look professional, win more bids with AI-powered estimates, and get paid faster with organized invoicing and a client portal.</p>
            </div>
            <div className="scroll-animate">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Is there a free trial?</h3>
              <p className="text-slate-600 leading-relaxed">Yes. You can start a free trial with no credit card required. Choose Basic, Pro, or Business and upgrade or change plans anytime.</p>
            </div>
            <div className="scroll-animate">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">What plans does Remodra offer?</h3>
              <p className="text-slate-600 leading-relaxed">Remodra offers Basic ($49/mo), Pro ($79/mo with AI estimates and roof reports), and Business ($149/mo with full team and agent management). All plans include a free trial.</p>
            </div>
            <div className="scroll-animate">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Can my clients view estimates and invoices online?</h3>
              <p className="text-slate-600 leading-relaxed">Yes. Clients get secure, token-based access to a portal where they can view estimates, approve them, see project status, and view or pay invoices 24/7.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-white border-t border-slate-200 py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/remodra-logo.png" alt="Remodra" className="h-12 w-12 rounded-full object-cover shadow-md" />
                <span className="text-slate-900 text-2xl font-bold">Remodra</span>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Professional contractor management platform designed to help you scale your business with confidence.
              </p>
            </div>
            <div className="bg-emerald-50/80 border border-emerald-200/60 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800 mb-2">Enterprise-Grade Security</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">
                    Your data is protected with bank-level encryption. We never sell or misuse your data.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs bg-emerald-500/15 text-emerald-700 border-emerald-400/30">
                      SSL Encrypted
                    </Badge>
                    <Badge variant="secondary" className="text-xs bg-emerald-500/15 text-emerald-700 border-emerald-400/30">
                      GDPR Compliant
                    </Badge>
                    <Badge variant="secondary" className="text-xs bg-emerald-500/15 text-emerald-700 border-emerald-400/30">
                      SOC 2 Certified
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                <Link to="/privacy" className="text-sm text-slate-600 hover:text-emerald-600 transition-colors font-medium hover:underline">
                  Privacy Policy
                </Link>
                <span className="hidden sm:inline text-slate-300">•</span>
                <Link to="/terms" className="text-sm text-slate-600 hover:text-emerald-600 transition-colors font-medium hover:underline">
                  Terms of Service
                </Link>
              </div>
              <p className="text-slate-500 text-xs sm:text-sm">
                © 2024 Remodra. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
} 